<template>
    <main class="layout__main--narrow layout__main--no-top-padding">
        <BaseHeader
            v-if="pending || headerImage"
            :placeholder="pending"
            :image="headerImage"
        />

        <BaseSection>
            <BaseBackButton
                v-if="page && page.parent && page.parent.uri"
                :to="`/${page.parent.uri}`"
            >
                {{ page.parent.title }}
            </BaseBackButton>
            <BaseBackButton
                v-else
                to="/offices/eau-section-office"
            >
                Sections
            </BaseBackButton>
        </BaseSection>

        <transition name="fade">
            <article
                v-if="!error && !notFound"
                class="bleed"
            >
                <BaseSection
                    v-if="!pending && page"
                    class="container-narrow"
                >
                    <template #title>
                        <h1>{{ page.title }}</h1>
                    </template>

                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="page.introText"
                        class="lead"
                        data-allow-anchor
                        v-html="page.introText"
                    />
                    <!-- eslint-enable vue/no-v-html -->

                    <template v-if="page.contactEmail">
                        <BaseButton
                            element="a"
                            :href="`mailto:${page.contactEmail}`"
                        >
                            Contact our section
                            <BaseIcon icon="envelope" />
                        </BaseButton>
                    </template>
                    <template v-if="page.contactPhone">
                        <BaseButton
                            element="a"
                            :href="`tel:${page.contactPhone}`"
                        >
                            Contact our section
                            <BaseIcon icon="phone" />
                        </BaseButton>
                    </template>
                </BaseSection>

                <BaseSection
                    v-if="!containsBoardOverviewBlock && boardMembers && boardMembers.length"
                    class="section section--wide section--extra-spacing"
                >
                    <template
                        v-if="pending"
                        #title
                    >
                        <h1>
                            <ContentPlaceholdersHeading :lines="2" />
                        </h1>
                    </template>
                    <BaseMemberList
                        :members="boardMembers"
                        :show-affiliates-associates="!page?.isASection"
                    />

                    <p
                        v-if="pending"
                        class="lead"
                    >
                        <ContentPlaceholdersText :lines="5" />
                    </p>
                </BaseSection>

                <FlexibleSections
                    :sections="page && page?.sections ? page.sections : []"
                    :placeholder="pending ? 'content' : undefined"
                    :members="boardMembers"
                    :show-affiliates-associates="!page?.isASection"
                />

                <BaseSection class="section--wide section--extra-spacing">
                    <template #title>
                        <h2>Upcoming events</h2>
                    </template>
                    <template #action>
                        <nuxt-link
                            to="/education-events/events"
                            aria-label="See all events"
                        >
                            See all <BaseIcon icon="chevron-right" />
                        </nuxt-link>
                    </template>

                    <template #default>
                        <related-events
                            :related-to-entry-id="page?.id"
                            event-type="live"
                        />
                    </template>
                </BaseSection>

                <BaseSection class="section--wide section--extra-spacing">
                    <template #title>
                        <h2>Related content</h2>
                    </template>
                    <template #action>
                        <nuxt-link
                            to="/news"
                            aria-label="See all news"
                        >
                            See all <BaseIcon icon="chevron-right" />
                        </nuxt-link>
                    </template>
                    <template #default>
                        <related-news :related-to-entry-id="page?.id" />
                    </template>
                </BaseSection>

                <BaseSection class="section--wide section--extra-spacing">
                    <template #title>
                        <h2>Start in your own time</h2>
                    </template>
                    <template #action>
                        <nuxt-link
                            to="/education-events/education"
                            aria-label="See all on-demand education"
                        >
                            See all <BaseIcon icon="chevron-right" />
                        </nuxt-link>
                    </template>
                    <template #default>
                        <related-events
                            :related-to-entry-id="page?.id"
                            event-type="ondemand"
                        />
                    </template>
                </BaseSection>
            </article>

            <BaseError
                v-else-if="error || notFound"
                :error="error || { statusCode: 404 }"
            />
        </transition>
    </main>
</template>

<script setup>
import DetailQuery from '~/graphql/queries/sections/Detail.graphql';

const { activeSite } = useMultisite();
const { variables } = useCraftGraphqlSlug();

const response = await useLazyAsyncQuery({
    query: DetailQuery,
    variables,
});


// todo: notfound
const {
    craftEntry: page,
    headerImage,
    pending,
    error
} = usePageQuery(
    response,
    { entryKey: 'page', activeSite }
);

const boardMembers = computed(() => page.value?.members?.filter((member) => {
    return (
        member.memberType !== 'Associate' &&
        member.memberType !== 'Affiliate'
    );
}));

// const associateMembers = member.memberType === 'Associate';

const containsBoardOverviewBlock = computed(() =>
    page.value?.sections?.some((section) => section.__typename === 'flexibleSection_boardOverview_BlockType')
);

</script>
